import { isMobileDevice } from "@/lib/utils";
import { BlendedAudioAdapter } from "../infrastructure/adapters/BlendedAudioAdapter";
import { WebAudioAdapter } from "../infrastructure/adapters/WebAudioAdapter";
import {
	type IAudioRepository,
	AudioRepository,
} from "../infrastructure/AudioRepository";
import type { AppManager } from "./AppManager";
import type { AudioPlaybackService } from "./ports";
import type { AppStore } from "./stores/appStore";
import { AdapterRegistry } from "./stores/AudioAdapterRegistry";
import {
	playbackStore,
	type PlaybackStoreProps,
	usePlaybackStore,
	type PlaybackStore,
} from "./stores/playbackStore";
import { StreamingAudioContextAdapter } from "../infrastructure/adapters/StreamingAudioContextAdapter";
import { AudioContextAdapter } from "../infrastructure/adapters/AudioContextAdapter";
import type { AudioModel, BaseAudioModel } from "@coffitivity/shared";

export interface AudioManagerArgs {
	appStore: AppStore;
	showModalHandler: AppManager["showModal"];
}

export class AudioManager {
	private static instance: AudioManager | null = null;
	private adapterRegistry: typeof AdapterRegistry;
	private showModal: AppManager["showModal"];
	private appStore: AppStore;
	private isMobile = false;
	private _areAdTracksPreloaded = false;
	private adTracks: AudioModel[] = [];

	audioRepository: IAudioRepository = new AudioRepository();
	store: PlaybackStore;

	useStore: () => PlaybackStoreProps;

	private constructor(_args: AudioManagerArgs) {
		this.store = playbackStore;
		this.useStore = usePlaybackStore;
		this.adapterRegistry = AdapterRegistry;
		this.appStore = _args.appStore;
		this.showModal = _args.showModalHandler;
		this.isMobile = isMobileDevice();

		// add a custom listener to the playbackStore to update the volume when it changes
		this.store.subscribe(
			(state) => state.masterVolume,
			(newMasterVolume) => {
				AudioContextAdapter.getInstance().setMasterVolume(newMasterVolume);
			},
		);
	}

	/**
	 * Singleton access to the instance of the AudioManager.
	 */
	static getInstance(_args: AudioManagerArgs): AudioManager {
		if (!AudioManager.instance) {
			AudioManager.instance = new AudioManager(_args);
		}
		return AudioManager.instance;
	}

	private isAdTrack(track: AudioModel | BaseAudioModel): boolean {
		return track.type === "ad";
	}

	/**
	 * We need to tie a human gesture and preload the ad tracks
	 * so that we can play them programmatically. Otherwise, mobile browsers
	 * will block the audio from playing.
	 */
	private async preloadAdTracks(): Promise<void> {
		const adTracks = await this.audioRepository.getAdTracks();
		this.adTracks = adTracks;
		for (const track of adTracks) {
			const adapter = this.adapterRegistry.get(track.id);
			if (!adapter) {
				this.getOrCreateAudioAdapter(track as AudioModel);
			}
		}
		this._areAdTracksPreloaded = true;
	}

	/**
	 * Returns an audio adapter by track ID. This method expose direct access to the
	 * playback controls to the app.
	 *
	 * @param track - The track to get the adapter for.
	 * @returns {Promise<AudioPlaybackService>} The audio adapter for the track.
	 */
	async getOrCreateAudioAdapter(
		track: AudioModel | BaseAudioModel,
	): Promise<AudioPlaybackService> {
		if (this.adapterRegistry.get(track.id)) {
			return this.adapterRegistry.get(track.id)!;
		}

		let adapter: AudioPlaybackService;
		if (track.type === "blend") {
			adapter = new BlendedAudioAdapter({
				store: this.store,
				masterVolume: this.store.getState().masterVolume,
				preferredMimeType: this.store.getState().preferredMimeType,
				track: track as AudioModel,
			});
		} else {
			if (this.isMobile) {
				adapter = new WebAudioAdapter({
					store: this.store,
					track,
					masterVolume: this.store.getState().masterVolume,
					preferredMimeType: this.store.getState().preferredMimeType,
				});
			} else {
				adapter = new StreamingAudioContextAdapter({
					store: this.store,
					track,
					masterVolume: this.store.getState().masterVolume,
					preferredMimeType: this.store.getState().preferredMimeType,
				});
			}
		}

		await adapter.load();

		this.adapterRegistry.register(track.id, adapter);

		return adapter;
	}

	masterVolume(): number {
		return this.store.getState().masterVolume;
	}

	// Pause the currently playing track
	async pauseTrack(baseTrackId?: string, blendTrackId?: string): Promise<void> {
		const currentStatus = this.store.getState().status;

		// if the status is already paused, return
		if (currentStatus === "PAUSED") return;

		if (baseTrackId) {
			const adapter = this.adapterRegistry.get(baseTrackId);
			if (adapter) {
				await adapter.pause(blendTrackId);
			}
		} else {
			const currentTrack = this.store.getState().currentTrack;

			if (!currentTrack) {
				return;
			}

			const adapter = await this.getOrCreateAudioAdapter(currentTrack);
			await adapter.pause();
		}
	}

	async playAdTrack(_onEndCallback: () => void | Promise<void>): Promise<void> {
		const randomAdTrack =
			this.adTracks[Math.floor(Math.random() * this.adTracks.length)];
		const adapter = await this.getOrCreateAudioAdapter(
			randomAdTrack as AudioModel,
		);

		this.store.getState().moveCurrentTrackToPrevious();
		this.store.getState().setCurrentTrack(randomAdTrack);

		adapter.onEnded = async () => {
			this.store.getState().movePreviousTrackToCurrent();
			await this.playTrack();
			await _onEndCallback();
		};

		await adapter.play();
	}

	async playTrack(
		_baseTrack?: AudioModel,
		_blendTrackId?: string,
	): Promise<void> {
		const currentTrack = this.store.getState().currentTrack;
		const isSubscribed = this.appStore.getState().isUserSubscribed;

		// Make sure our ad tracks are preloaded
		if (!this._areAdTracksPreloaded) {
			this.preloadAdTracks();
		}

		// PREMIUM CHECK ----------
		// check if the user is subscribed and the track is premium
		if (!isSubscribed && _baseTrack?.type !== "ad" && _baseTrack?.isPremium) {
			// show the upgrade modal
			this.showModal("UPGRADE_MODAL", true);
			// don't play the track
			return;
		}

		// AD CHECK ---------------
		// if the current track is an ad they need to finish it before
		// playing a new track
		if (currentTrack && this.isAdTrack(currentTrack)) {
			const adapter = await this.getOrCreateAudioAdapter(currentTrack);
			await adapter.play();
			return;
		}

		// pause the currently playing track if it's not the same as the new one
		if (
			currentTrack?.id !== _baseTrack?.id &&
			this.store.getState().status === "PLAYING"
		) {
			this.pauseTrack(currentTrack?.id);
		}

		// try to get the new track if one was passed in
		if (_baseTrack) {
			const track = await this.getOrCreateAudioAdapter(_baseTrack);

			if (_blendTrackId) {
				await track.play(_blendTrackId);
				return;
			}

			await track.play();
			return;
		}

		// if no track was passed in and there is no current track, return
		// should never happen
		if (!currentTrack) {
			return;
		}

		// find the current track in the registry and play it
		const adapter = await this.getOrCreateAudioAdapter(currentTrack);
		await adapter.play();
	}

	setMasterVolume(volume: number): void {
		this.store.getState().setVolume(volume);
	}

	toggleMute(): void {
		this.store.getState().toggleMute();
	}

	// async unlock(): Promise<void> {
	// 	if (this.isUnlocked) return;
	// 	if (this.audioContext.state === "suspended") {
	// 		await this.audioContext.resume();
	// 	}
	// 	this.isUnlocked = true;
	// }
}

export type AudioManagerType = AudioManager;
